/**
 * Product scrollspy — `sections/product-scrollspy.liquid`.
 *
 * Three jobs, in this order:
 *
 * 1. **Resolve the destinations.** Each link names a CSS selector rather than a section
 *    id, because a section removed and re-added in the theme editor comes back with a new
 *    id and a link pointing at the old one would scroll nowhere. A link whose selector
 *    matches nothing is removed outright; if none match, the whole capsule stays hidden.
 * 2. **Give the anchors real hrefs.** They are rendered without one — Liquid has no way to
 *    know what the other sections on the template will be called. Assigning an id here and
 *    pointing the anchor at it is what makes these links rather than click handlers on
 *    text: focusable, announced as links, and still meaningful if this script's own click
 *    handler ever failed.
 * 3. **Track the section under the capsule** and mark its link `aria-current="location"`.
 *
 * The reveal is gated on all of that succeeding — `[data-ready]` is set last, and the
 * stylesheet keeps the capsule `display: none` until it appears. A product page with no
 * JavaScript simply has no in-page nav, which costs nothing: every section it points at is
 * on the page and reachable by scrolling.
 */

(function () {
  'use strict';

  /* The reference's `data-gap` default, and ours. Only used if the attribute is missing
     or unparseable — the section always writes it. */
  const DEFAULT_GAP = 20;

  let uid = 0;

  class ScrollSpy extends HTMLElement {
    connectedCallback() {
      this.nav = this.querySelector('.product-scrollspy__nav');
      this.topButton = this.querySelector('[data-scrollspy-top]');
      if (!this.nav) return;

      const gap = parseInt(this.dataset.gap, 10);
      this.gap = Number.isNaN(gap) ? DEFAULT_GAP : gap;

      this.entries = [];
      this.activeLink = null;
      this.ticking = false;
      this.onScroll = this.onScroll.bind(this);
      this.onClick = this.onClick.bind(this);

      this.resolve();
      if (!this.entries.length && !this.topButton) return;

      this.addEventListener('click', this.onClick);
      window.addEventListener('scroll', this.onScroll, { passive: true });

      this.setAttribute('data-ready', '');
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    /**
     * Pairs every link with the element it points at, dropping the ones with nothing
     * behind them.
     *
     * Presence in the document is the test, deliberately — not height. `product-
     * recommendations` is on the page from first paint but fills itself over the network,
     * and only once the shopper is within 400px of it (`component-recommendations.js`).
     * Measuring height here would hide "You may also like" on every product page for
     * exactly as long as the link is worth having, and reveal it at the moment the shopper
     * no longer needs it. Its worst case is the opposite and far milder: the API returns
     * nothing, the block stays empty, and the link scrolls to where it would have been.
     */
    resolve() {
      this.querySelectorAll('[data-scrollspy-target]').forEach((link) => {
        const target = document.querySelector(link.dataset.scrollspyTarget);

        if (!target) {
          link.closest('.product-scrollspy__item').remove();
          return;
        }

        if (!target.id) {
          uid += 1;
          target.id = `scrollspy-target-${uid}`;
        }

        link.href = `#${target.id}`;
        this.entries.push({ link, target });
      });

      /* `update()` reads these in order and keeps the last one to pass under the capsule,
         so they have to be in document order — which is not necessarily the order the
         merchant arranged the links in. Compared by position rather than by `offsetTop`,
         because a section that has not filled yet has no height but does have a place. */
      this.entries.sort((a, b) =>
        a.target.compareDocumentPosition(b.target) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
      );
    }

    /**
     * How far below the viewport top a section has to stop.
     *
     * The header, the capsule itself and the authored gap. The reference omits the
     * capsule's own height and so parks each heading underneath it; including it means the
     * heading a shopper just asked for is the first thing they see.
     */
    get offset() {
      const header = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height')
      );

      return (Number.isNaN(header) ? 0 : header) + this.nav.offsetHeight + this.gap;
    }

    onClick(event) {
      const top = event.target.closest('[data-scrollspy-top]');
      if (top) {
        event.preventDefault();
        this.scrollTo(0);
        return;
      }

      const link = event.target.closest('[data-scrollspy-target]');
      if (!link) return;

      const entry = this.entries.find((item) => item.link === link);
      if (!entry) return;

      /* The default jump lands the section at the very top of the viewport, behind both
         the header and this capsule. Everything below replaces it — including the hash,
         which is pushed rather than assigned so Back returns the shopper to where they
         were reading instead of to the top of the page. */
      event.preventDefault();
      this.scrollTo(window.scrollY + entry.target.getBoundingClientRect().top - this.offset);

      if (window.history && window.history.pushState) {
        window.history.pushState(null, '', link.getAttribute('href'));
      }

      /* Moves the reading position, not just the scroll position: without this a keyboard
         or screen-reader user is scrolled to a section their focus is nowhere near, and the
         next Tab goes back to whatever followed the link. `preventScroll` because the
         scroll is already under way and the browser's own would fight the smooth one. */
      entry.target.setAttribute('tabindex', '-1');
      entry.target.focus({ preventScroll: true });
    }

    scrollTo(top) {
      window.scrollTo({
        top,
        behavior: window.Hubcart && window.Hubcart.prefersReducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;

      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    }

    /**
     * Marks the last section whose top has passed under the capsule.
     *
     * The bottom of the document is a special case: the final section is often shorter
     * than the remaining scroll, so its top never reaches the line and it would never
     * light up no matter how far the shopper scrolled. At the end of the page it is the
     * one they are looking at, so it wins.
     */
    update() {
      if (!this.entries.length) return;

      const line = this.offset + 1;
      const atBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;

      let active = null;

      if (atBottom) {
        active = this.entries[this.entries.length - 1].link;
      } else {
        this.entries.forEach(({ link, target }) => {
          if (target.getBoundingClientRect().top <= line) active = link;
        });
      }

      if (active === this.activeLink) return;

      if (this.activeLink) this.activeLink.removeAttribute('aria-current');
      if (active) active.setAttribute('aria-current', 'location');
      this.activeLink = active;
    }
  }

  if (!customElements.get('scroll-spy')) customElements.define('scroll-spy', ScrollSpy);
})();
